import { logAppEvent } from "@/api/logs";
import { useDialog } from "@/components/DialogProvider";
import { ProjectHoursTable } from "@/components/project-hours-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelectPopover } from "@/components/ui/searchable-select-popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { SpecialtyAutocomplete } from "@/components/ui/specialty-autocomplete";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { WorkerSchedulesTab } from "@/components/worker-schedules-tab";
import { WorkerAssignedProjects } from "@/components/workers/WorkerAssignedProjects";
import { useAgencies } from "@/features/agencies/hooks/useAgencies";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useSmsHistory } from "@/features/sms/hooks/useSmsHistory";
import { useUsers } from "@/features/users/hooks/useUsers";
import { workUnitService } from "@/features/work-units/api/workUnitService";
import { getProjectWorkerAssignments } from "@/lib/projectAssignmentHelpers";
import { formatPhoneNumber } from "@/lib/utils";
import { logException } from "@/logException";
import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { doc, onSnapshot, Timestamp, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  AlertCircle,
  ArrowLeft,
  Building,
  Camera,
  Car,
  CheckCircle,
  Clock,
  Copy,
  CreditCard,
  DollarSign,
  Download,
  Edit,
  FileText,
  Image,
  KeyRound,
  Languages,
  MapPin,
  MessageSquare,
  RefreshCw,
  RotateCw,
  Save,
  Trash2,
  Upload,
  User,
  Wrench,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { formatDateTime } from "../../../../../api/datetime";
import { db, functions, storage } from "../../../../../firebase";
import { isWorkerAssignedToProject, type WorkerModel } from "../../../../../firestore_types";

export const Route = createFileRoute("/dashboard/admin/workers/$workerId/")({
  component: WorkerProfile,
});

interface WorkerAttachment {
  id: string;
  name: string;
  type: "image" | "pdf";
  url: string;
  uploadedAt?: Timestamp | Date;
  size: number;
}

function WorkerSmsHistory({ phoneNumber }: { phoneNumber: string; workerId: string }) {
  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, refetch } = useSmsHistory(
    50,
    phoneNumber,
  );
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [retrySuccess, setRetrySuccess] = useState<string | null>(null);

  const messages = data?.pages.flatMap((page) => page.messages) ?? [];

  const handleRetry = async (smsId: string) => {
    setRetryingId(smsId);
    setRetryError(null);
    setRetrySuccess(null);

    try {
      const retrySmsCallable = httpsCallable(functions, "retrySmsCallable");
      const result = await retrySmsCallable({ smsId });
      const data = result.data as { success: boolean; error?: string; messageId?: string };

      if (data.success) {
        setRetrySuccess(`Message retried successfully!`);
        setTimeout(() => {
          refetch();
          setRetrySuccess(null);
        }, 2000);
      } else {
        throw new Error(data.error || "Failed to retry message");
      }
    } catch (error) {
      console.error("Error retrying SMS:", error);
      setRetryError(error instanceof Error ? error.message : "Failed to retry message");
    } finally {
      setRetryingId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "sent":
      case "delivered":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "failed":
      case "undelivered":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "pending":
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusVariant = (
    status: string,
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status.toLowerCase()) {
      case "sent":
      case "delivered":
        return "default";
      case "failed":
      case "undelivered":
        return "destructive";
      case "pending":
        return "secondary";
      default:
        return "outline";
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">SMS History</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="mb-3 h-16 w-full" />
                <Skeleton className="h-3 w-1/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">SMS History for {formatPhoneNumber(phoneNumber)}</h2>
        <Button
          onClick={() => refetch()}
          disabled={isFetching}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {retrySuccess && (
        <div className="mb-4 rounded-lg bg-green-50 p-4 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-400">
          {retrySuccess}
        </div>
      )}

      {retryError && (
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span>{retryError}</span>
          </div>
        </div>
      )}

      {messages.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <MessageSquare className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
            <p className="text-muted-foreground">No SMS messages found for this worker</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {messages.map((message, index) => (
              <Card key={`${message.id}-${index}`} className="transition-shadow hover:shadow-md">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <Badge
                      variant={getStatusVariant(message.status)}
                      className="flex flex-shrink-0 items-center gap-1"
                    >
                      {getStatusIcon(message.status)}
                      {message.status}
                    </Badge>
                  </div>
                  <div className="text-muted-foreground flex items-center gap-1 text-xs">
                    <Clock className="h-3 w-3" />
                    <span>{formatDateTime(message.createdAt)}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted/50 mb-3 rounded-lg p-3">
                    <p className="text-sm leading-relaxed break-words">{message.message}</p>
                  </div>

                  {message.error && (
                    <div className="mb-3 rounded-lg bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400">
                      <span className="font-medium">Error: </span>
                      <span className="line-clamp-2">{message.error}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-2">
                    <div className="space-y-1">
                      {message.updatedAt && message.updatedAt !== message.createdAt && (
                        <div className="text-muted-foreground text-xs">
                          Updated {formatDateTime(message.updatedAt)}
                        </div>
                      )}
                      {message.retryCount !== undefined && message.retryCount > 0 && (
                        <div className="text-muted-foreground text-xs">
                          Retry attempts: {message.retryCount} / 5
                        </div>
                      )}
                    </div>

                    {message.status === "failed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRetry(message.id)}
                        disabled={retryingId === message.id || (message.retryCount || 0) >= 5}
                        className="flex items-center gap-1"
                      >
                        <RotateCw
                          className={`h-3 w-3 ${retryingId === message.id ? "animate-spin" : ""}`}
                        />
                        {retryingId === message.id ? "Retrying..." : "Retry"}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {hasNextPage && (
            <div className="flex justify-center pt-4">
              <Button
                onClick={() => fetchNextPage()}
                disabled={isFetching}
                variant="outline"
                className="flex items-center gap-2"
              >
                {isFetching ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WorkerProfile() {
  const { workerId } = Route.useParams();
  const { confirm } = useDialog();
  const navigate = useNavigate();
  const router = useRouter();
  const { projects } = useProjects();
  const { users } = useUsers();
  const { agencies } = useAgencies();
  const [worker, setWorker] = useState<WorkerModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState<WorkerAttachment[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
  const [smsMessage, setSmsMessage] = useState("");
  const [sendingSms, setSendingSms] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    managerId: "",
    agencyId: "",
    payRate: "",
    billingRate: "",
    specialties: [] as string[],
    gender: "" as "" | "Male" | "Female",
    location: "",
    englishLevel: "",
    hasDriverLicense: "" as "" | "Yes" | "No",
    hasCar: "" as "" | "Yes" | "No",
    notes: "",
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "Workers", workerId),
      (workerDoc) => {
        if (workerDoc.exists()) {
          const workerData = {
            ...workerDoc.data(),
            id: workerDoc.id,
          } as WorkerModel;
          setWorker(workerData);

          // Initialize edit form data
          setEditFormData({
            name: workerData.name || "",
            phoneNumber: workerData.phoneNumber || "",
            email: workerData.email || "",
            managerId: workerData.managerId || "",
            agencyId: workerData.agencyId || "",
            payRate: workerData.payRate?.toString() || "",
            billingRate: workerData.billingRate?.toString() || "",
            specialties: workerData.specialties || [],
            gender: workerData.gender || "",
            location: workerData.location || "",
            englishLevel: workerData.englishLevel || "",
            hasDriverLicense: workerData.hasDriverLicense || "",
            hasCar: workerData.hasCar || "",
            notes: workerData.notes || "",
          });

          // Load attachments if they exist
          if (workerData.attachments) {
            setAttachments(workerData.attachments);
          }
        } else {
          setWorker(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching worker:", error);
        toast.error("Failed to load worker profile");
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [workerId]);

  const getAssignedProjects = () => {
    return (projects || []).filter((project) => isWorkerAssignedToProject(project, workerId));
  };

  const getInactiveAssignedProjects = () => {
    return (projects || []).filter((project) => {
      const assignments = getProjectWorkerAssignments(project);
      return assignments.some((a) => a.workerId === workerId && a.inactive);
    });
  };

  const getManagerName = (managerId?: string) => {
    if (!managerId) {
      return "No Manager";
    }
    const manager = (users || []).find((user) => user.uid === managerId && user.type === "manager");
    return manager?.name || "Unknown Manager";
  };

  const getAgencyName = (agencyId: string) => {
    if (!agencyId) return "No Agency";
    const agency = (agencies || []).find((agency) => agency.id === agencyId);
    return agency?.name || "Unknown Agency";
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (file.size > maxSize) {
      toast.info("File size must be less than 10MB");
      return;
    }

    const allowedTypes = ["application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      toast.info("Only PDF files are allowed");
      return;
    }

    const fileName = `${Date.now()}-${file.name}`;
    setUploadingFiles((prev) => [...prev, fileName]);

    try {
      const storageRef = ref(storage, `workers/${workerId}/attachments/${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      const newAttachment: WorkerAttachment = {
        id: fileName,
        name: file.name,
        type: "pdf",
        url: downloadURL,
        uploadedAt: new Date(),
        size: file.size,
      };

      const updatedAttachments = [...attachments, newAttachment];
      setAttachments(updatedAttachments);

      // Update worker document with new attachments
      await updateDoc(doc(db, "Workers", workerId), {
        attachments: updatedAttachments,
      });

      toast.info("File uploaded successfully");
    } catch (error) {
      console.error("Error uploading file:", error);
      logException(error, {
        extra: {
          context: "worker_attachment_upload",
          workerId,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        },
      });
      toast.info("Failed to upload file");
    } finally {
      setUploadingFiles((prev) => prev.filter((f) => f !== fileName));
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!(await confirm({ description: "Are you sure you want to delete this attachment?" })))
      return;

    try {
      // Delete from Firebase Storage
      const storageRef = ref(storage, `workers/${workerId}/attachments/${attachmentId}`);
      await deleteObject(storageRef);

      // Update local state and Firestore
      const updatedAttachments = attachments.filter((att) => att.id !== attachmentId);
      setAttachments(updatedAttachments);

      await updateDoc(doc(db, "Workers", workerId), {
        attachments: updatedAttachments,
      });

      toast.info("Attachment deleted successfully");
    } catch (error) {
      console.error("Error deleting attachment:", error);
      logException(error, {
        extra: {
          context: "worker_attachment_delete",
          workerId,
          attachmentId,
        },
      });
      toast.info("Failed to delete attachment");
    }
  };

  const handleProfilePictureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (file.size > maxSize) {
      toast.info("Profile picture size must be less than 5MB");
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.info("Only images (JPEG, PNG, GIF) are allowed for profile pictures");
      return;
    }

    setUploadingProfilePicture(true);

    try {
      const fileName = `profile-${Date.now()}.${file.name.split(".").pop()}`;
      const storageRef = ref(storage, `workers/${workerId}/profile/${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Delete old profile picture if it exists
      if (worker?.profilePicture) {
        try {
          const oldRef = ref(storage, worker.profilePicture);
          await deleteObject(oldRef);
        } catch (error) {
          console.warn("Could not delete old profile picture:", error);
        }
      }

      // Update worker document with new profile picture
      await updateDoc(doc(db, "Workers", workerId), {
        profilePicture: downloadURL,
      });

      // Update local state
      setWorker((prev) => (prev ? { ...prev, profilePicture: downloadURL } : null));

      toast.info("Profile picture updated successfully");
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      logException(error, {
        extra: {
          context: "worker_profile_picture_upload",
          workerId,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        },
      });
      toast.info("Failed to upload profile picture");
    } finally {
      setUploadingProfilePicture(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!worker?.profilePicture) return;
    if (!(await confirm({ description: "Are you sure you want to remove the profile picture?" })))
      return;

    try {
      // Delete from Firebase Storage
      const storageRef = ref(storage, worker.profilePicture);
      await deleteObject(storageRef);

      // Update worker document
      await updateDoc(doc(db, "Workers", workerId), {
        profilePicture: null,
      });

      // Update local state
      setWorker((prev) => (prev ? { ...prev, profilePicture: null } : null));

      toast.info("Profile picture removed successfully");
    } catch (error) {
      console.error("Error removing profile picture:", error);
      logException(error, {
        extra: {
          context: "worker_profile_picture_remove",
          workerId,
          profilePictureUrl: worker.profilePicture,
        },
      });
      toast.info("Failed to remove profile picture");
    }
  };

  const handleToggleAssignment = async (projectId: string, isCurrentlyInactive: boolean) => {
    if (
      !(await confirm({
        description: `Are you sure you want to ${isCurrentlyInactive ? "activate" : "deactivate"} this assignment?`,
      }))
    )
      return;

    try {
      const project = (projects || []).find((p) => p.id === projectId);
      if (!project) return;

      if (project.workUnits) {
        // Find which work unit contains this worker
        const workUnit = project.workUnits.find((wu) =>
          wu.attachedWorkers?.some((w) => w.workerId === workerId),
        );
        if (!workUnit) {
          toast.error("Worker not found in any work unit");
          return;
        }

        await workUnitService.toggleWorkerStatus(
          projectId,
          workUnit.id,
          workerId,
          isCurrentlyInactive,
        );
      }

      toast.info(`Assignment ${isCurrentlyInactive ? "activated" : "deactivated"} successfully`);
    } catch (error) {
      console.error("Error toggling assignment:", error);
      toast.info("Failed to update assignment status");
    }
  };

  const handleSendSms = () => {
    setSmsMessage("");
    setIsSmsModalOpen(true);
  };

  const submitSms = async () => {
    if (!worker || !smsMessage.trim()) return;

    setSendingSms(true);
    try {
      const sendSmsCallable = httpsCallable(functions, "sendSmsCallable");
      const result = await sendSmsCallable({
        phoneNumber: worker.phoneNumber,
        message: smsMessage,
        workerId: worker.id,
        isTestNumber: worker.isTestNumber || false,
      });

      const data = result.data as {
        success: boolean;
        messageId?: string;
        error?: string;
      };

      if (data.success) {
        setIsSmsModalOpen(false);
        setSmsMessage("");

        logAppEvent("sms_sent", {
          msg: smsMessage,
          workerName: worker.name,
        });

        toast.info("SMS sent successfully!");
      } else {
        throw new Error(data.error || "Failed to send SMS");
      }
    } catch (error) {
      console.error("Error sending SMS:", error);
      toast.info("Failed to send SMS. Please try again.");
    } finally {
      setSendingSms(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirm({ description: "Are you sure you want to delete this worker?" }))) return;

    try {
      // Remove worker from all assigned projects first
      const assignedProjects = getAssignedProjects();
      if (assignedProjects.length > 0) {
        for (const project of assignedProjects) {
          if (project.workUnits) {
            await workUnitService.removeWorkerFromAll(project.id, workerId);
          }
        }
      }

      const workerRef = doc(db, "Workers", workerId);
      await updateDoc(workerRef, {
        isDeleted: true,
        updatedAt: Timestamp.now(),
      });
      logAppEvent("worker_deleted", { workerId });
      toast.info("Worker deleted successfully");
      navigate({ to: "/dashboard/admin/workers" });
    } catch (error) {
      console.error("Error deleting worker:", error);
      toast.error("Failed to delete worker. Please try again.");
    }
  };

  const handleEdit = () => {
    if (!worker) return;

    // Reset form data to current worker data
    setEditFormData({
      name: worker.name || "",
      phoneNumber: worker.phoneNumber || "",
      email: worker.email || "",
      managerId: worker.managerId || "",
      agencyId: worker.agencyId || "",
      payRate: worker.payRate?.toString() || "",
      billingRate: worker.billingRate?.toString() || "",
      specialties: worker.specialties || [],
      gender: worker.gender || "",
      location: worker.location || "",
      englishLevel: worker.englishLevel || "",
      hasDriverLicense: worker.hasDriverLicense || "",
      hasCar: worker.hasCar || "",
      notes: worker.notes || "",
    });
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    if (!worker) return;

    // Reset form data
    setEditFormData({
      name: worker.name || "",
      phoneNumber: worker.phoneNumber || "",
      email: worker.email || "",
      managerId: worker.managerId || "",
      agencyId: worker.agencyId || "",
      payRate: worker.payRate?.toString() || "",
      billingRate: worker.billingRate?.toString() || "",
      specialties: worker.specialties || [],
      gender: worker.gender || "",
      location: worker.location || "",
      englishLevel: worker.englishLevel || "",
      hasDriverLicense: worker.hasDriverLicense || "",
      hasCar: worker.hasCar || "",
      notes: worker.notes || "",
    });
  };

  const handleSaveEdit = async () => {
    if (!worker) return;

    setIsSaving(true);
    try {
      const workerRef = doc(db, "Workers", workerId);
      await updateDoc(workerRef, {
        name: editFormData.name,
        phoneNumber: editFormData.phoneNumber,
        email: editFormData.email || null,
        managerId: editFormData.managerId,
        agencyId: editFormData.agencyId || null,
        payRate: editFormData.payRate ? parseFloat(editFormData.payRate) : null,
        billingRate: editFormData.billingRate ? parseFloat(editFormData.billingRate) : null,
        specialties: editFormData.specialties.length > 0 ? editFormData.specialties : null,
        gender: editFormData.gender || null,
        location: editFormData.location || null,
        englishLevel: editFormData.englishLevel || null,
        hasDriverLicense: editFormData.hasDriverLicense || null,
        hasCar: editFormData.hasCar || null,
        notes: editFormData.notes || null,
        updatedAt: Timestamp.now(),
      });

      logAppEvent("worker_updated", {
        workerId,
        name: editFormData.name,
        phoneNumber: editFormData.phoneNumber,
      });

      toast.info("Worker updated successfully");
      setIsEditMode(false);
    } catch (error) {
      console.error("Error updating worker:", error);
      toast.error("Failed to update worker. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetPassword = async () => {
    if (!passwordValue.trim()) {
      toast.error("Password cannot be empty");
      return;
    }
    setIsSavingPassword(true);
    try {
      await updateDoc(doc(db, "Workers", workerId), { appPassword: passwordValue.trim() });
      toast.success(worker?.appPassword ? "Password updated successfully" : "Password set successfully");
      setIsPasswordDialogOpen(false);
      setPasswordValue("");
    } catch (error) {
      console.error("Error saving app password:", error);
      toast.error("Failed to save password");
    } finally {
      setIsSavingPassword(false);
    }
  };

  const getManagerOptions = () => {
    return (users || []).filter((user) => user.type === "manager");
  };

  const handleCopyClockInLink = async () => {
    if (!worker?.workerCode) return;

    const clockInUrl = `https://app.gethandle.com/worker/${worker.workerCode}`;
    try {
      await navigator.clipboard.writeText(clockInUrl);
      toast.info("Clock-in link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast.error("Failed to copy link. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">Loading worker profile...</div>
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold">Worker not found</h2>
          <p className="text-gray-600">The worker you are looking for does not exist.</p>
        </div>
      </div>
    );
  }

  const assignedProjects = getAssignedProjects();

  return (
    <div className="p-1 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => router.history.back()}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Worker Profile - {worker.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          {isEditMode ? (
            <>
              <Button
                variant="default"
                size="sm"
                onClick={handleSaveEdit}
                disabled={isSaving}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="mr-2 h-4 w-4" />
                {isSaving ? "Saving..." : "Save"}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={handleCopyClockInLink}>
                <Copy className="mr-2 h-4 w-4" />
                Copy Clock-in Link
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPasswordValue("");
                  setIsPasswordDialogOpen(true);
                }}
              >
                <KeyRound className="mr-2 h-4 w-4" />
                {worker.appPassword ? "Update Password" : "Set App Password"}
              </Button>
              <Button variant="outline" size="sm" onClick={handleEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
              {!worker.isTestNumber && worker.type !== "app_worker" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-green-600 hover:text-green-700"
                  onClick={handleSendSms}
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Send SMS
                </Button>
              )}
              {!(worker.lastClockedInAt && worker.lastClockedOutAt) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                  onClick={handleDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="schedules">Schedules</TabsTrigger>
          <TabsTrigger value="time_entries">Time Entries</TabsTrigger>
          <TabsTrigger value="sms_history">SMS History</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Main Profile Info */}
            <div className="space-y-6 lg:col-span-2">
              {/* Profile Picture Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Profile Picture</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProfilePictureUpload}
                        className="hidden"
                        id="profile-picture-upload"
                      />
                      <Button
                        size="sm"
                        onClick={() => document.getElementById("profile-picture-upload")?.click()}
                        disabled={uploadingProfilePicture}
                      >
                        <Camera className="mr-2 h-4 w-4" />
                        {worker?.profilePicture ? "Change" : "Upload"}
                      </Button>
                      {worker?.profilePicture && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleRemoveProfilePicture}
                          disabled={uploadingProfilePicture}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center">
                    {uploadingProfilePicture ? (
                      <div className="flex h-32 w-32 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
                      </div>
                    ) : worker?.profilePicture ? (
                      <img
                        src={worker.profilePicture}
                        alt={`${worker.name} profile`}
                        className="h-32 w-32 rounded-lg border-2 border-gray-200 object-cover"
                      />
                    ) : (
                      <div className="flex h-32 w-32 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                        <div className="text-center">
                          <User className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                          <p className="text-sm text-gray-500">No profile picture</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Worker Info Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{worker.name}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant={worker.isTestNumber ? "secondary" : "default"}>
                        {worker.isTestNumber ? "Test" : "Live"}
                      </Badge>
                      {worker.type === "app_worker" && <Badge variant="outline">App Worker</Badge>}
                    </div>
                  </CardTitle>
                  <CardDescription>
                    Worker ID: {worker.id}
                    {worker.workerCode && <> · Worker Code: {worker.workerCode}</>}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditMode ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          value={editFormData.name}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, name: e.target.value })
                          }
                          placeholder="Enter worker name"
                        />
                      </div>
                      <div>
                        <Label>Phone Number *</Label>
                        <Input
                          value={editFormData.phoneNumber}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, phoneNumber: e.target.value })
                          }
                          placeholder="Enter phone number"
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={editFormData.email}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, email: e.target.value })
                          }
                          placeholder="Enter email address"
                        />
                      </div>
                      <div>
                        <Label>Manager *</Label>
                        <SearchableSelectPopover
                          items={getManagerOptions()}
                          selectedValue={editFormData.managerId}
                          onSelect={(value) =>
                            setEditFormData({ ...editFormData, managerId: value || "" })
                          }
                          getKey={(manager) => manager.uid}
                          getLabel={(manager) => manager.name}
                          triggerLabel={
                            editFormData.managerId
                              ? getManagerName(editFormData.managerId)
                              : "Select Manager"
                          }
                          placeholder="Search managers..."
                          emptyText="No managers found"
                          buttonClassName="w-full justify-between"
                          contentClassName="w-full p-0"
                        />
                      </div>
                      <div>
                        <Label>Agency</Label>
                        <SearchableSelectPopover
                          items={agencies || []}
                          selectedValue={editFormData.agencyId}
                          onSelect={(value) =>
                            setEditFormData({ ...editFormData, agencyId: value || "" })
                          }
                          getKey={(agency) => agency.id}
                          getLabel={(agency) => agency.name}
                          triggerLabel={
                            editFormData.agencyId
                              ? getAgencyName(editFormData.agencyId)
                              : "No Agency"
                          }
                          placeholder="Search agencies..."
                          emptyText="No agencies found"
                          allOption={{ value: "", label: "No Agency" }}
                          buttonClassName="w-full justify-between"
                          contentClassName="w-full p-0"
                        />
                      </div>
                      <div>
                        <Label>Pay Rate ($/hr)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editFormData.payRate}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, payRate: e.target.value })
                          }
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label>Billing Rate ($/hr)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editFormData.billingRate}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, billingRate: e.target.value })
                          }
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label>Gender</Label>
                        <select
                          value={editFormData.gender}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              gender: e.target.value as "" | "Male" | "Female",
                            })
                          }
                          className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Input
                          value={editFormData.location}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, location: e.target.value })
                          }
                          placeholder="Enter location"
                        />
                      </div>
                      <div>
                        <Label>English Level</Label>
                        <Select
                          value={editFormData.englishLevel || "none"}
                          onValueChange={(value) =>
                            setEditFormData({
                              ...editFormData,
                              englishLevel: value === "none" ? "" : value,
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select English level..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Not specified</SelectItem>
                            <SelectItem value="No English">No English</SelectItem>
                            <SelectItem value="Basic">Basic</SelectItem>
                            <SelectItem value="Conversational">Conversational</SelectItem>
                            <SelectItem value="Fluent">Fluent</SelectItem>
                            <SelectItem value="Native">Native</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Driver&apos;s License</Label>
                        <select
                          value={editFormData.hasDriverLicense}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              hasDriverLicense: e.target.value as "" | "Yes" | "No",
                            })
                          }
                          className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                        >
                          <option value="">Not specified</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                      <div>
                        <Label>Has Car</Label>
                        <select
                          value={editFormData.hasCar}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              hasCar: e.target.value as "" | "Yes" | "No",
                            })
                          }
                          className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                        >
                          <option value="">Not specified</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <Label>Notes</Label>
                        <Textarea
                          value={editFormData.notes}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, notes: e.target.value })
                          }
                          placeholder="Additional notes about the worker..."
                          rows={4}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Phone Number</label>
                        <p className="mt-1">{formatPhoneNumber(worker.phoneNumber)}</p>
                      </div>
                      {worker.email && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Email</label>
                          <p className="mt-1">{worker.email}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-gray-600">Manager</label>
                        <p className="mt-1">{getManagerName(worker.managerId)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Agency</label>
                        <div className="mt-1 flex items-center gap-2">
                          <Building className="h-4 w-4 text-gray-400" />
                          {worker.agencyId ? (
                            <Link
                              to="/dashboard/admin/agencies/$agencyId"
                              params={{ agencyId: worker.agencyId }}
                              className="cursor-pointer text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                            >
                              {getAgencyName(worker.agencyId)}
                            </Link>
                          ) : (
                            <span>{getAgencyName("")}</span>
                          )}
                        </div>
                      </div>
                      {worker.payRate && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Pay Rate</label>
                          <div className="mt-1 flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-green-600" />
                            <span>${worker.payRate.toFixed(2)}/hr</span>
                          </div>
                        </div>
                      )}
                      {worker.billingRate && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Billing Rate</label>
                          <div className="mt-1 flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-blue-600" />
                            <span>${worker.billingRate.toFixed(2)}/hr</span>
                          </div>
                        </div>
                      )}
                      {worker.gender && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Gender</label>
                          <p className="mt-1">{worker.gender}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-gray-600">Created At</label>
                        <p className="mt-1">{formatDateTime(worker.createdAt)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Last Updated</label>
                        <p className="mt-1">{formatDateTime(worker.updatedAt)}</p>
                      </div>
                      {worker.location && (
                        <div>
                          <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-600">
                            <MapPin className="h-4 w-4" />
                            Location
                          </label>
                          <p className="mt-1">{worker.location}</p>
                        </div>
                      )}
                      {worker.englishLevel && (
                        <div>
                          <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-600">
                            <Languages className="h-4 w-4" />
                            English Level
                          </label>
                          <p className="mt-1">{worker.englishLevel}</p>
                        </div>
                      )}
                      {worker.hasDriverLicense && (
                        <div>
                          <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-600">
                            <CreditCard className="h-4 w-4" />
                            Driver&apos;s License
                          </label>
                          <p className="mt-1">{worker.hasDriverLicense}</p>
                        </div>
                      )}
                      {worker.hasCar && (
                        <div>
                          <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-600">
                            <Car className="h-4 w-4" />
                            Has Car
                          </label>
                          <p className="mt-1">{worker.hasCar}</p>
                        </div>
                      )}
                      {worker.notes && (
                        <div className="md:col-span-2">
                          <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-600">
                            <FileText className="h-4 w-4" />
                            Notes
                          </label>
                          <p className="mt-1 whitespace-pre-wrap">{worker.notes}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <Separator />

                  {isEditMode ? (
                    <div>
                      <Label>Worker experience</Label>
                      <div className="space-y-2">
                        <SpecialtyAutocomplete
                          value=""
                          onChange={(specialty) => {
                            if (specialty && !editFormData.specialties.includes(specialty)) {
                              setEditFormData({
                                ...editFormData,
                                specialties: [...editFormData.specialties, specialty],
                              });
                            }
                          }}
                          placeholder="Add Experience"
                        />
                        <div className="flex flex-wrap gap-2">
                          {editFormData.specialties.map((specialty) => (
                            <Badge key={specialty} variant="outline" className="text-sm">
                              {specialty}
                              <button
                                type="button"
                                onClick={() =>
                                  setEditFormData({
                                    ...editFormData,
                                    specialties: editFormData.specialties.filter(
                                      (s) => s !== specialty,
                                    ),
                                  })
                                }
                                className="ml-1 text-red-500 hover:text-red-700"
                              >
                                ×
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    worker.specialties &&
                    worker.specialties.length > 0 && (
                      <div>
                        <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-600">
                          <Wrench className="h-4 w-4" />
                          Worker experience
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {worker.specialties.map((specialty) => (
                            <Badge key={specialty} variant="outline" className="text-xs">
                              {specialty}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )
                  )}

                  <Separator />

                  <div>
                    <label className="text-sm font-medium text-gray-600">Clock Status</label>
                    <div className="mt-2 space-y-1">
                      {worker.lastClockedInAt && (
                        <p className="text-sm">
                          Last clocked in: {formatDateTime(worker.lastClockedInAt)}
                        </p>
                      )}
                      {worker.lastClockedOutAt && (
                        <p className="text-sm">
                          Last clocked out: {formatDateTime(worker.lastClockedOutAt)}
                        </p>
                      )}
                      {!worker.lastClockedInAt && !worker.lastClockedOutAt && (
                        <p className="text-sm text-gray-500">No clock activity yet</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Assigned Projects */}
              <WorkerAssignedProjects
                workerId={workerId}
                workerName={worker.name}
                assignedProjects={assignedProjects}
                inactiveProjects={getInactiveAssignedProjects()}
                allProjects={projects || []}
                onToggleAssignment={handleToggleAssignment}
              />
            </div>

            {/* Attachments Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Attachments</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="attachment-upload"
                      />
                      <Button
                        size="sm"
                        onClick={() => document.getElementById("attachment-upload")?.click()}
                        disabled={uploadingFiles.length > 0}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload
                      </Button>
                    </div>
                  </CardTitle>
                  <CardDescription>Upload PDF documents only</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {uploadingFiles.map((fileName) => (
                      <div
                        key={fileName}
                        className="flex items-center gap-3 rounded-lg border bg-gray-50 p-3"
                      >
                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
                        <span className="text-sm text-gray-600">Uploading...</span>
                      </div>
                    ))}

                    {attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-gray-50"
                      >
                        <div className="flex-shrink-0">
                          {attachment.type === "image" ? (
                            <Image className="h-5 w-5 text-blue-600" />
                          ) : (
                            <FileText className="h-5 w-5 text-red-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <a
                            href={attachment.url}
                            target="_blank"
                            className="truncate text-sm font-medium hover:text-blue-600"
                            rel="noreferrer"
                          >
                            {attachment.name}
                          </a>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(attachment.url, "_blank")}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteAttachment(attachment.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    {attachments.length === 0 && uploadingFiles.length === 0 && (
                      <p className="py-4 text-center text-sm text-gray-500">
                        No attachments yet. Upload some files to get started.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="schedules" className="mt-6">
          <WorkerSchedulesTab workerId={workerId} workerName={worker.name} />
        </TabsContent>

        <TabsContent value="time_entries" className="mt-6">
          <ProjectHoursTable
            workerIds={[workerId]}
            showAgencyNote={true}
            allowClickableProjects={true}
            allowDeleteEntries={true}
            allowApproval={true}
          />
        </TabsContent>

        <TabsContent value="sms_history" className="mt-6">
          <WorkerSmsHistory phoneNumber={worker.phoneNumber} workerId={workerId} />
        </TabsContent>
      </Tabs>

      {/* SMS Dialog */}
      <Dialog open={isSmsModalOpen} onOpenChange={setIsSmsModalOpen}>
        <DialogContent className="mx-4 w-full max-w-md sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Send SMS to {worker.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1 block text-sm font-medium text-gray-700">Phone Number</Label>
              <input
                type="text"
                value={formatPhoneNumber(worker.phoneNumber)}
                disabled
                className="w-full rounded border bg-gray-100 px-3 py-2"
              />
            </div>
            <div>
              <Label className="mb-1 block text-sm font-medium text-gray-700">Message</Label>
              <Textarea
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                className="h-24 w-full resize-none rounded border px-3 py-2"
                placeholder="Enter your message..."
                maxLength={160}
              />
              <div className="mt-1 text-right text-xs text-gray-500">{smsMessage.length}/160</div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setIsSmsModalOpen(false);
                setSmsMessage("");
              }}
              variant="outline"
              disabled={sendingSms}
            >
              Cancel
            </Button>
            <Button
              onClick={submitSms}
              disabled={!smsMessage.trim() || sendingSms}
              className="bg-green-500 hover:bg-green-600"
            >
              {sendingSms ? "Sending..." : "Send SMS"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="mx-4 w-full max-w-sm sm:mx-auto">
          <DialogHeader>
            <DialogTitle>
              {worker.appPassword ? "Update App Password" : "Set App Password"} for {worker.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1 block text-sm font-medium text-gray-700">New Password</Label>
              <Input
                type="text"
                value={passwordValue}
                onChange={(e) => setPasswordValue(e.target.value)}
                placeholder="Enter app password"
                autoComplete="off"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setIsPasswordDialogOpen(false);
                setPasswordValue("");
              }}
              variant="outline"
              disabled={isSavingPassword}
            >
              Cancel
            </Button>
            <Button onClick={handleSetPassword} disabled={!passwordValue.trim() || isSavingPassword}>
              {isSavingPassword ? "Saving..." : worker.appPassword ? "Update Password" : "Set Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
